import { useStore } from '@nanostores/react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { motion } from 'framer-motion';
import { memo, useCallback } from 'react';
import { toast } from 'react-toastify';
import { AccountDisplayWithMembership } from '~/components/ui/AccountDisplay';
import { AccountSelectMenuContent, AccountSelectMenuItem } from '~/components/ui/AccountSelectMenu';
import { UserAvatar } from '~/components/ui/UserAvatar';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { chatStore } from '~/lib/stores/chat';
import { teamPricingPlansStore } from '~/lib/stores/pricing/teamPlans';
import { userStore } from '~/lib/stores/user';
import { AccountSelectedTrigger, AnalyticsAction, AnalyticsTrackEvent, CreateTeamTrigger } from '~/types/analytics';
import type { OrganizationData } from '~/types/organization';
import type { UserData } from '~/types/user';
import { dropdownAnimationVariants } from '~/utils/animationVariants';

interface AccountSelectorMenuProps {
  user: UserData;
  activeOrganization: OrganizationData | null;
}

export const AccountSelectorMenu = memo(({ user, activeOrganization }: AccountSelectorMenuProps) => {
  const selectableOrgs = useStore(userStore.selectableOrganizations);

  const onSelectAccount = async (organization: OrganizationData | null) => {
    sendAnalyticsEvent({
      action: AnalyticsAction.Track,
      payload: {
        event: AnalyticsTrackEvent.AccountSelected,
        properties: {
          from: AccountSelectedTrigger.Menu,
        },
      },
    });
    userStore.setActiveOrganization(organization);

    try {
      await chatStore.loadChatList();
    } catch {
      toast.error('Failed to load your chats');
    }
  };

  const createTeam = useCallback(() => {
    sendAnalyticsEvent({
      action: AnalyticsAction.Track,
      payload: {
        event: AnalyticsTrackEvent.CreateTeamMenuClicked,
        properties: {
          from: CreateTeamTrigger.Menu,
        },
      },
    });

    teamPricingPlansStore.startCreateTeamFlow();
  }, []);

  return (
    <DropdownMenu.Portal>
      <AccountSelectMenuContent side="right" align="end" asChild>
        <motion.div initial="close" animate="open" variants={dropdownAnimationVariants}>
          {activeOrganization && (
            <AccountSelectMenuItem onClick={() => onSelectAccount(null)}>
              <AccountDisplayWithMembership
                type="user"
                accountName={user.name ?? user.username}
                membershipLevel={user.membership?.tier}
                avatar={user.avatar}
              />
            </AccountSelectMenuItem>
          )}
          {selectableOrgs.map((organization) => (
            <AccountSelectMenuItem key={organization.id} onClick={() => onSelectAccount(organization)}>
              <AccountDisplayWithMembership
                type="organization"
                user={user}
                accountName={user.name ?? user.username}
                membershipLevel={organization.billing?.tier}
                orgName={organization?.name}
                avatar={organization?.avatar}
              />
            </AccountSelectMenuItem>
          ))}
          <AccountSelectMenuItem onClick={createTeam}>
            <UserAvatar iconClassName="i-ph:users-three" />
            <div>Create a team</div>
          </AccountSelectMenuItem>
        </motion.div>
      </AccountSelectMenuContent>
    </DropdownMenu.Portal>
  );
});
